import { getNumberFormatLocale } from "~/stores/number-format-store"

export const INVENTORY_COST_TOOLTIP_TYPE = "InventoryCost"

export type InventoryCostTooltipValue = { tooltipType: typeof INVENTORY_COST_TOOLTIP_TYPE } & (
  | {
      level: "variant"
      units: number
      /** Rounded, unweighted average of the variant's SKU cost prices; null when none is set. */
      costPrice: number | null
      value: number
    }
  | { level: "sku"; units: number; costPrice: number; value: number }
  | { level: "group"; value: number }
)

/** A missing variant average prices the stock at 0. */
export const variantInventoryCost = (units: number, averageCostPrice: number | null | undefined) =>
  units * (averageCostPrice ?? 0)

/** A SKU without its own cost price has no value; it never borrows the variant average. */
export const skuInventoryCost = (units: number, costPrice: number | null | undefined) =>
  costPrice == null ? "N/A" : units * costPrice

const FORMULA: Record<InventoryCostTooltipValue["level"], string> = {
  variant: "closing stock × average SKU cost price",
  sku: "closing stock × SKU cost price",
  group: "sum of variant values",
}

function costNote(tooltip: InventoryCostTooltipValue): string | undefined {
  switch (tooltip.level) {
    case "variant":
      return tooltip.costPrice == null
        ? "No SKU has a cost price, so the value is 0."
        : "A plain average of SKU cost prices, rounded to 2 decimals. SKU rows use their own prices, so they can sum differently."
    case "sku":
      return undefined
    case "group":
      return "Each variant uses its own average SKU cost price."
  }
}

export const formatCostPrice = (costPrice: number | null) =>
  costPrice == null
    ? "No cost price"
    : costPrice.toLocaleString(getNumberFormatLocale(), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })

/** Formula and caveat shown in a Cost cell's tooltip. */
export const describeInventoryCost = (tooltip: InventoryCostTooltipValue) => ({
  formula: FORMULA[tooltip.level],
  note: costNote(tooltip),
})
